/**
 * The payment **gateway** seam for multi-cart checkout. A provider (Stripe,
 * manual, or an in-memory fake) satisfies {@link PaymentGateway}; the
 * {@link payForCarts} orchestrator drives it without knowing which one it is.
 */

import type { BankTransferDetails } from "@/lib/bank-transfer";

/**
 * One prepared means-of-payment for a single cart. Opaque to the orchestrator:
 * a Stripe PaymentMethod id, or an empty sentinel for a no-op (manual)
 * instrument. One per cart - Stripe Connect forbids reusing a PaymentMethod
 * across connected accounts.
 */
export interface PaymentInstrument {
  paymentMethodId: string;
}

/** The per-cart payment plan and its lifecycle status. */
export interface CartPaymentSession {
  cartId: string;
  sellerName: string;
  /**
   * The server-side payment session this plan was initiated against, handed to
   * `completeCart` so the backend settles *this* session or refuses. See
   * `CONTEXT.md` -> "Provider-session pairing".
   *
   * Optional because the initiate response can carry no session for the chosen
   * provider; `payForCarts` refuses such a plan rather than completing an
   * unnamed one.
   */
  paymentSessionId?: string;
  /** Only present for Stripe-based providers. */
  clientSecret?: string;
  /**
   * Only present for the bank-transfer provider: the marketplace payee details
   * the buyer needs in order to pay. Marketplace-level config, so every cart's
   * session carries the same values.
   */
  bankTransfer?: BankTransferDetails;
  providerId: string;
  status: "pending" | "ready" | "confirming" | "confirmed" | "error";
  error?: string;
}

/**
 * The seam a payment provider satisfies.
 *
 * - `prepare(count)` creates exactly `count` payment instruments. For Stripe
 *   this runs `elements.submit()` + N `createPaymentMethod` calls, which **must**
 *   happen synchronously in the click handler before any other async work; for
 *   manual it returns `count` no-op instruments.
 * - `confirm(clientSecret, instrument)` settles one cart's payment and throws on
 *   failure. Manual is a no-op.
 */
export interface PaymentGateway {
  prepare(count: number): Promise<PaymentInstrument[]>;
  confirm(
    clientSecret: string | undefined,
    instrument: PaymentInstrument
  ): Promise<void>;
}

/**
 * The payment provider declined or rejected the payment and said why in words
 * meant for the buyer ("Your card was declined", "Your card number is
 * incomplete"). Its message is shown as-is and is not an app fault, so it is not
 * reported. Anything else a gateway throws is an unexpected failure.
 */
export class PaymentFailedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PaymentFailedError";
  }
}

/**
 * A gateway together with the provider it was built for.
 *
 * These two are meaningless apart - the gateway is chosen *from* the provider
 * id - and pairing them at the point of construction is what stops them being
 * passed around separately and drifting. {@link payForCarts} still re-checks the
 * sessions against `providerId` before charging; this only narrows what that
 * check has to defend against.
 */
export interface SelectedGateway {
  providerId: string;
  gateway: PaymentGateway;
}

/**
 * The manual (pay-on-delivery / arranged-with-seller) provider: there is nothing
 * to prepare or confirm, so `prepare` returns one no-op instrument per cart -
 * keeping the `sessions[i] <-> instruments[i]` zip length-safe - and `confirm`
 * does nothing.
 */
export const manualGateway: PaymentGateway = {
  prepare: (count) =>
    Promise.resolve(
      Array.from({ length: count }, () => ({ paymentMethodId: "" }))
    ),
  confirm: () => Promise.resolve()
};
